import cn from 'clsx';
import { FC, HTMLAttributes, memo, MouseEvent, useCallback, useMemo } from 'react';

import useCurrentState from '../../hooks/dialog/useCurrentState';

import CSSTransition from './CSSTransition';

interface Props {
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  visible?: boolean;
  preventDefault?: boolean;
  maxWidth?: 'default' | 'small' | 'large';
  animated?: boolean;
  scss: Record<string, string>;
}

type NativeAttrs = Omit<HTMLAttributes<HTMLDivElement>, keyof Props>;

export type BackdropProps = Props & NativeAttrs;

const Backdrop: FC<React.PropsWithChildren<React.PropsWithChildren<BackdropProps>>> = memo(
  ({ children, onClick = () => {}, visible = false, maxWidth = 'default', animated = true, preventDefault = true, scss, ...props }) => {
    const [, setIsContentMouseDown, IsContentMouseDownRef] = useCurrentState(false);
    const clickHandler = useCallback(
      (event: MouseEvent<HTMLElement>) => {
        if (IsContentMouseDownRef.current) return;
        if (onClick) {
          onClick(event);
        }
      },
      [onClick, IsContentMouseDownRef]
    );

    const childrenClickHandler = useCallback((event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
    }, []);

    const handleMouseUp = useCallback(() => {
      if (!IsContentMouseDownRef.current) return;
      const timer = setTimeout(() => {
        setIsContentMouseDown(false);
        clearTimeout(timer);
      }, 0);
    }, [setIsContentMouseDown, IsContentMouseDownRef]);

    const handleMouseDown = useCallback(() => {
      setIsContentMouseDown(true);
    }, [setIsContentMouseDown]);

    const getState = useMemo(() => {
      return visible ? 'open' : 'closed';
    }, [visible]);

    const renderChildren = useMemo(() => {
      return (
        <div tabIndex={-1} aria-hidden data-state={getState} onClick={clickHandler} onMouseUp={handleMouseUp} className={scss.backdrop} {...props}>
          <div className={scss.backdrop__layer} />
          <div
            tabIndex={-1}
            aria-hidden
            onClick={childrenClickHandler}
            onMouseDown={handleMouseDown}
            className={cn(scss.backdrop__content, scss[`backdrop__content--${maxWidth}`])}
          >
            {children}
          </div>
        </div>
      );
    }, [children, getState, clickHandler, handleMouseUp, handleMouseDown, childrenClickHandler, maxWidth]);

    const conditionalRender = useMemo(() => {
      if (animated) {
        return (
          <CSSTransition name="backdrop-css" visible={visible} enterTime={100} leaveTime={100} clearTime={100} scss={scss} className="">
            {renderChildren}
          </CSSTransition>
        );
      }
      return visible ? renderChildren : null;
    }, [animated, visible, renderChildren]);

    return conditionalRender;
  }
);
Backdrop.displayName = 'Backdrop';
export default Backdrop;
